import { DateTime } from "luxon";
import { useI18n } from "vue-i18n";

import type { Option } from "@/Components/UI/TcSelect.vue";
import { Language } from "@/Common/Models/Domain/Language";

import { Day } from "@/Common/Models/Domain/Day";

import { hasValue } from "@/Utils/Objects";

import { danger } from "./Debug";

export const MILLISECONDS_TO_SECONDS = 1000;
export const WEEKS_TO_SECONDS = 604800;
export const DAY_IN_SECONDS = 86400;

let APP_LOCALE = "en-gb";

export const getNowInSeconds = (): number => Math.floor(Date.now() / 1000);

export const setLocaleApp = (locale: string) => {
  APP_LOCALE = locale === Language.ENGLISH ? "en-gb" : locale;
};

export const getAppLocale = () => APP_LOCALE;

export const getCurrentDateInSeconds = () => Math.floor(Date.now() / MILLISECONDS_TO_SECONDS);

export const getTranslatedMonthName = (monthNumber: number) => {
  return DateTime.local().setLocale(getAppLocale()).set({ month: monthNumber }).monthLong;
};

export const dateToDateUi = (
  date: Date | number | string,
  format = "dd/MM/yyyy",
  userTimeZone = getTimeZone()
): string => {
  let luxonDate: DateTime;

  if (date instanceof Date) {
    luxonDate = DateTime.fromJSDate(date);
  } else if (["number", "string"].includes(typeof date)) {
    const jsDate = unixSecondsToDate(date);
    luxonDate = DateTime.fromJSDate(jsDate);
  } else {
    danger("Invalid date transformation type UNSUPPORTED", date);

    return "";
  }

  return luxonDate.setZone(userTimeZone).setLocale(getAppLocale()).toFormat(format);
};

export const dateToHoursAndMinutes = (date: Date) => {
  const formattedMinutes = date.getMinutes().toString().padStart(2, "0");
  const formattedHours = date.getHours().toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
};

export const dateToFullDate = (date: Date) => {
  return `${dateToDateUi(date)}, ${dateToHoursAndMinutes(date)}`;
};

export const unixSecondsToDateTime = (
  seconds: number,
  { zone = "utc", translated = true }: { zone?: string; translated?: boolean } = {}
): DateTime => {
  if (translated) {
    return DateTime.fromMillis(seconds * MILLISECONDS_TO_SECONDS, { zone }).setLocale(getAppLocale());
  }

  return DateTime.fromMillis(seconds * MILLISECONDS_TO_SECONDS, { zone });
};

export const localeDateFormatFromDateTime = (
  datetime: DateTime,
  hour = true,
  minute = true,
  custom = true
): string => {
  const { t } = useI18n();
  const date = datetime
    .toLocaleString({
      weekday: "long",
      month: "long",
      day: "numeric",
    })
    .replace(new RegExp(",", "gi"), "");
  const time = datetime.toLocaleString({
    hourCycle: hour || minute ? "h24" : undefined,
    hour: hour ? "2-digit" : undefined,
    minute: minute ? "2-digit" : undefined,
  });

  if (hour && minute)
    return custom
      ? `${date} ${t("schedule.therapist.appointment_summary.date_union")} ${time}`
      : `${date}, ${time}`;

  return date;
};

export function unixSecondsToDate(unixSeconds: number | string): Date {
  let value = 0;

  if (typeof unixSeconds === "string") {
    try {
      value = parseInt(unixSeconds, 10);

      if (Number.isNaN(value)) value = 0;
    } catch (e) {
      danger("Error conversión");
      danger(e);
    }
  } else {
    value = unixSeconds;
  }

  return new Date(value * MILLISECONDS_TO_SECONDS);
}

export function unixUTC(): number {
  return DateTime.utc().toUnixInteger();
}

export const dateToUnixUTC = (date: Date): number => {
  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );

  return DateTime.fromJSDate(new Date(now_utc))
    .plus({ minutes: -1 * date.getTimezoneOffset() })
    .toUnixInteger();
};

export const dateToUnix = (date: Date): number => {
  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );

  return DateTime.fromJSDate(new Date(now_utc)).toUnixInteger();
};

const secondsToUTCDate = (seconds: number) => {
  return DateTime.fromJSDate(unixSecondsToDate(seconds)).toUTC();
};

export const secondsToDuration = (seconds: number) => {
  return secondsToUTCDate(seconds).toFormat("HH:mm:ss");
};

export const getDateLabelFromUnixSeconds = (seconds: number): Day => {
  const currentDate = DateTime.local().toUTC().startOf("day");
  const dateFromParam = secondsToUTCDate(seconds).startOf("day");

  const days = dateFromParam.diff(currentDate, "days").days;
  const weeks = currentDate.startOf("week").diff(dateFromParam, "weeks").weeks;
  const months = currentDate.startOf("month").diff(dateFromParam, "months").months;

  if (!hasValue(days) || !hasValue(weeks) || !hasValue(months)) {
    return Day.ANOTHER_DAY;
  }

  if (days > 0 && days <= 1) {
    return Day.TOMORROW;
  } else if (days < 0 && days >= -1) {
    return Day.YESTERDAY;
  } else if (days === 0) {
    return Day.TODAY;
  } else if (weeks <= 0) {
    return Day.THIS_WEEK;
  } else if (months <= 0) {
    return Day.THIS_MONTH;
  }

  return Day.ANOTHER_DAY;
};

export const getDaysNextMonths = (
  date: Date,
  weekDay: number,
  nMonths: number,
  searchPre: boolean
): Option<number>[] => {
  let auxDate = dateToUnix(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
  const finalDate = dateToUnix(new Date(date.getFullYear(), date.getMonth() + nMonths, date.getDate()));
  const days: Option<number>[] = [];
  let counterDays = 0;

  while (counterDays !== -1 && searchPre && unixSecondsToDate(auxDate).getDay() !== weekDay) {
    if (unixSecondsToDate(auxDate).getDay() === weekDay) {
      days.push({
        label: dateToDateUi(unixSecondsToDate(auxDate), "dd/MM/yy"),
        value: auxDate,
      });

      auxDate += DAY_IN_SECONDS * counterDays;
      counterDays = -2;
    }

    auxDate -= DAY_IN_SECONDS;
    counterDays++;
  }

  while (auxDate < finalDate) {
    if (unixSecondsToDate(auxDate).getDay() === weekDay) {
      days.push({
        label: dateToDateUi(unixSecondsToDate(auxDate), "dd/MM/yy"),
        value: auxDate,
      });
    }

    auxDate += DAY_IN_SECONDS;
  }

  return days;
};

export const dateToSecondsOnlyDate = (date: Date): number => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  return new Date(year, month, day).getTime();
};

export const dateBackendFormat = (date: Date): number => {
  return date.getTime() / MILLISECONDS_TO_SECONDS;
};

export const addDaysToDate = (date: Date, days: number): Date => {
  return DateTime.fromJSDate(date).plus({ days: days }).toJSDate();
};

export const getTimeZone = (): string => {
  return DateTime.local().zoneName;
};

export const isWithinTimeLimit = (timestamp: number, limitMs: number): boolean => {
  return timestamp >= Date.now() - limitMs;
};
